import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import authService from '../services/auth.service';

const Sidebar: React.FC = () => {
  const location = useLocation();
  const isAdmin = authService.hasRole('admin');
  const canManageIntakeFormTypes =
    isAdmin || authService.hasPermission('manage_intake_form_types');
  const canViewCases = isAdmin || authService.hasRole('firm_admin');
  const canAccessAdmin = isAdmin || authService.hasRole('firm_admin');

  const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

  const isActive = (path: string) => {
    return (
      location.pathname === path || location.pathname.startsWith(`${path}/`)
    );
  };

  const toggleSubmenu = (menu: string) => {
    setOpenSubmenu(openSubmenu === menu ? null : menu);
  };

  return (
    <aside className='app-sidebar'>
      <div className='sidebar-header'>
        <Link to='/' className='sidebar-brand'>
          <img
            src='/assets/images/brand-logos/pve-logo.png'
            alt='Logo'
            className='sidebar-brand-img desktop-logo'
          />
          <img
            src='/assets/images/brand-logos/pve-white.png'
            alt='Logo'
            className='sidebar-brand-img desktop-dark'
          />
        </Link>
        <button className='sidebar-toggle d-lg-none'>
          <i className='ri-menu-line'></i>
        </button>
      </div>

      <div className='sidebar-body'>
        <ul className='sidebar-nav'>
          {canAccessAdmin ? (
            // Admin Navigation
            <>
              <li className='nav-label'>ADMIN</li>
              <li
                className={`nav-item ${isActive('/dashboard') && location.pathname === '/dashboard' ? 'active' : ''}`}
              >
                <Link to='/dashboard' className='nav-link'>
                  <i className='ri-dashboard-line'></i>
                  <span>Dashboard</span>
                </Link>
              </li>

              {isAdmin && (
                <li
                  className={`nav-item ${isActive('/admin/firms') ? 'active' : ''}`}
                >
                  <Link to='/admin/firms' className='nav-link'>
                    <i className='ri-building-line'></i>
                    <span>Law Firms</span>
                  </Link>
                </li>
              )}

              {isAdmin && (
                <li
                  className={`nav-item ${isActive('/admin/users') ? 'active' : ''} ${openSubmenu === 'user-management' ? 'open' : ''}`}
                >
                  <a
                    href='#'
                    className='nav-link'
                    onClick={e => {
                      e.preventDefault();
                      toggleSubmenu('user-management');
                    }}
                  >
                    <i className='ri-team-line'></i>
                    <span>User Management</span>
                    <i className='ri-arrow-down-s-line ms-auto'></i>
                  </a>
                  <ul className='nav-submenu'>
                    <li
                      className={`nav-submenu-item ${isActive('/admin/users') ? 'active' : ''}`}
                    >
                      <Link
                        to='/admin/users'
                        className='nav-submenu-link'
                      >
                        <i className='ri-user-line'></i>
                        <span>Users</span>
                      </Link>
                    </li>
                  </ul>
                </li>
              )}

              {canViewCases && (
                <li
                  className={`nav-item ${isActive('/admin/cases') ? 'active' : ''}`}
                >
                  <Link to='/admin/cases' className='nav-link'>
                    <i className='ri-folder-line'></i>
                    <span>Cases</span>
                  </Link>
                </li>
              )}

              {isAdmin && (
                <li
                  className={`nav-item ${isActive('/admin/financial-reports') ? 'active' : ''}`}
                >
                  <Link to='/admin/financial-reports' className='nav-link'>
                    <i className='ri-bar-chart-line'></i>
                    <span>Financial Reports</span>
                  </Link>
                </li>
              )}

              {isAdmin && (
                <li
                  className={`nav-item ${isActive('/admin/service-types') || isActive('/admin/attachments') || isActive('/admin/intake-form-types') || isActive('/admin/masters') ? 'active' : ''} ${openSubmenu === 'master' ? 'open' : ''}`}
                >
                  <a
                    href='#'
                    className='nav-link'
                    onClick={e => {
                      e.preventDefault();
                      toggleSubmenu('master');
                    }}
                  >
                    <i className='ri-settings-4-line'></i>
                    <span>Master</span>
                    <i className='ri-arrow-down-s-line ms-auto'></i>
                  </a>
                  <ul className='nav-submenu'>
                    <li
                      className={`nav-submenu-item ${isActive('/admin/masters/designations') ? 'active' : ''}`}
                    >
                      <Link to='/admin/masters/designations' className='nav-submenu-link'>
                        <i className='ri-user-settings-line'></i>
                        <span>Designations</span>
                      </Link>
                    </li>
                    <li
                      className={`nav-submenu-item ${isActive('/admin/masters/opportunity-case-types') ? 'active' : ''}`}
                    >
                      <Link to='/admin/masters/opportunity-case-types' className='nav-submenu-link'>
                        <i className='ri-list-check'></i>
                        <span>Opportunity Case Types</span>
                      </Link>
                    </li>
                    <li
                      className={`nav-submenu-item ${isActive('/admin/masters/sales-stages') ? 'active' : ''}`}
                    >
                      <Link to='/admin/masters/sales-stages' className='nav-submenu-link'>
                        <i className='ri-flag-line'></i>
                        <span>Sales Stages</span>
                      </Link>
                    </li>
                    <li
                      className={`nav-submenu-item ${isActive('/admin/service-types') ? 'active' : ''}`}
                    >
                      <Link
                        to='/admin/service-types'
                        className='nav-submenu-link'
                      >
                        <i className='ri-folder-settings-line'></i>
                        <span>Service Types</span>
                      </Link>
                    </li>
                    {canManageIntakeFormTypes && (
                      <li
                        className={`nav-submenu-item ${isActive('/admin/intake-form-types') ? 'active' : ''}`}
                      >
                        <Link
                          to='/admin/intake-form-types'
                          className='nav-submenu-link'
                        >
                          <i className='ri-file-list-3-line'></i>
                          <span>Intake Form Types</span>
                        </Link>
                      </li>
                    )}
                    <li
                      className={`nav-submenu-item ${isActive('/admin/attachments') ? 'active' : ''}`}
                    >
                      <Link
                        to='/admin/attachments'
                        className='nav-submenu-link'
                      >
                        <i className='ri-attachment-2'></i>
                        <span>Documents</span>
                      </Link>
                    </li>
                    <li
                      className={`nav-submenu-item ${isActive('/admin/office-locations') ? 'active' : ''}`}
                    >
                      <Link
                        to='/admin/office-locations'
                        className='nav-submenu-link'
                      >
                        <i className='ri-map-pin-line'></i>
                        <span>Office Locations</span>
                      </Link>
                    </li>
                    <li
                      className={`nav-submenu-item ${isActive('/admin/experts') ? 'active' : ''}`}
                    >
                      <Link to='/admin/experts' className='nav-submenu-link'>
                        <i className='ri-user-star-line'></i>
                        <span>Experts</span>
                      </Link>
                    </li>
                    <li
                      className={`nav-submenu-item ${isActive('/admin/email-templates') ? 'active' : ''}`}
                    >
                      <Link to='/admin/email-templates' className='nav-submenu-link'>
                        <i className='ri-mail-line'></i>
                        <span>Email Templates</span>
                      </Link>
                    </li>
                    {isAdmin && (
                      <li
                        className={`nav-submenu-item ${isActive('/admin/email-logs') ? 'active' : ''}`}
                      >
                        <Link to='/admin/email-logs' className='nav-submenu-link'>
                          <i className='ri-mail-send-line'></i>
                          <span>Email Logs</span>
                        </Link>
                      </li>
                    )}
                  </ul>
                </li>
              )}
            </>
          ) : (
            // Regular User Navigation
            <>
              <li className='nav-label'>MAIN</li>
              <li
                className={`nav-item ${isActive('/dashboard') ? 'active' : ''}`}
              >
                <Link to='/dashboard' className='nav-link'>
                  <i className='ri-dashboard-line'></i>
                  <span>Dashboard</span>
                </Link>
              </li>

              <li className='nav-label'>LEGAL</li>
              {canViewCases && (
                <li
                  className={`nav-item ${isActive('/admin/cases') ? 'active' : ''}`}
                >
                  <Link to='/admin/cases' className='nav-link'>
                    <i className='ri-folder-line'></i>
                    <span>Cases</span>
                  </Link>
                </li>
              )}
              <li
                className={`nav-item ${isActive('/dashboard/cases') ? 'active' : ''}`}
              >
                <Link to='/dashboard/cases' className='nav-link'>
                  <i className='ri-folder-line'></i>
                  <span>My Cases</span>
                </Link>
              </li>
              <li
                className={`nav-item ${isActive('/documents') ? 'active' : ''}`}
              >
                <Link to='/documents' className='nav-link'>
                  <i className='ri-file-text-line'></i>
                  <span>Documents</span>
                </Link>
              </li>
              <li
                className={`nav-item ${isActive('/signatures') ? 'active' : ''}`}
              >
                <Link to='/signatures' className='nav-link'>
                  <i className='ri-pen-nib-line'></i>
                  <span>Signatures</span>
                </Link>
              </li>
            </>
          )}

          <li className='nav-label'>ACCOUNT</li>
          <li className={`nav-item ${isActive('/dashboard/profile') ? 'active' : ''}`}>
            <Link to='/dashboard/profile' className='nav-link'>
              <i className='ri-user-line'></i>
              <span>Profile</span>
            </Link>
          </li>
          {isAdmin && (
            <li className={`nav-item ${isActive('/admin/settings') ? 'active' : ''}`}>
              <Link to='/admin/settings' className='nav-link'>
                <i className='ri-settings-line'></i>
                <span>Settings</span>
              </Link>
            </li>
          )}
        </ul>
      </div>
    </aside>
  );
};

export default Sidebar;
